import React, { useState } from 'react';
import { RankingPoint } from '../types';

interface RankingChartProps {
  rankingsHistory: {
    '24H': RankingPoint[];
    '7D': RankingPoint[];
    '30D': RankingPoint[];
    '90D': RankingPoint[];
    '1Y': RankingPoint[];
    'ALL': RankingPoint[];
  };
  initialTimeframe?: '24H' | '7D' | '30D' | '90D' | '1Y' | 'ALL';
}

export const RankingChart: React.FC<RankingChartProps> = ({
  rankingsHistory = { '24H': [], '7D': [], '30D': [], '90D': [], '1Y': [], 'ALL': [] },
  initialTimeframe = '30D'
}) => {
  const [timeframe, setTimeframe] = useState<'24H' | '7D' | '30D' | '90D' | '1Y' | 'ALL'>(initialTimeframe);
  const [selectedCountry, setSelectedCountry] = useState('United States');
  const [selectedCategory, setSelectedCategory] = useState('Overall');
  const [hoveredPoint, setHoveredPoint] = useState<{ point: RankingPoint; x: number; y: number } | null>(null);

  const safeHistory = rankingsHistory || { '24H': [], '7D': [], '30D': [], '90D': [], '1Y': [], 'ALL': [] };
  const points = safeHistory[timeframe] || safeHistory['30D'] || [];

  // Determine min and max ranks for inverted Y axis (rank 1 is top)
  const ranks = points.map(p => p.rank);
  const minRank = Math.min(...ranks, 1);
  const maxRank = Math.max(...ranks, 10);
  
  // Padding on scale
  const yMin = 1;
  const yMax = Math.ceil(maxRank * 1.25);

  const svgWidth = 700;
  const svgHeight = 240;
  const padX = 45;
  const padY = 30;
  const graphWidth = svgWidth - padX * 2;
  const graphHeight = svgHeight - padY * 2;

  const getCoordinates = (index: number, rank: number) => {
    const x = padX + (index / Math.max(points.length - 1, 1)) * graphWidth;
    // Inverted: rank 1 is at padY (top), rank yMax is at padY + graphHeight (bottom)
    const norm = (rank - yMin) / Math.max(yMax - yMin, 1);
    const y = padY + norm * graphHeight;
    return { x, y };
  };

  // Generate SVG path string
  let pathD = '';
  let areaD = '';
  if (points.length > 0) {
    const coords = points.map((p, idx) => getCoordinates(idx, p.rank));
    pathD = `M ${coords[0].x} ${coords[0].y}`;
    for (let i = 1; i < coords.length; i++) {
      const prev = coords[i - 1];
      const curr = coords[i];
      const cpx1 = prev.x + (curr.x - prev.x) / 2;
      const cpx2 = cpx1;
      pathD += ` C ${cpx1} ${prev.y}, ${cpx2} ${curr.y}, ${curr.x} ${curr.y}`;
    }
    const lastCoord = coords[coords.length - 1];
    areaD = `${pathD} L ${lastCoord.x} ${padY + graphHeight} L ${coords[0].x} ${padY + graphHeight} Z`;
  }

  // Y-axis ticks
  const yTicks = [1, Math.round(yMax / 3), Math.round((yMax * 2) / 3), yMax];

  return (
    <div className="bg-white rounded-3xl p-6 border border-slate-200/80 shadow-sm">
      {/* Top Header & Controls */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4 pb-5 border-b border-slate-100">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold text-slate-900 tracking-tight">Historical Rankings</h3>
            <span className="text-xs bg-emerald-50 text-emerald-700 font-semibold px-2 py-0.5 rounded-md border border-emerald-200">
              # {points[points.length - 1]?.rank || 1} Latest
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-0.5">
            Store rank trajectory across selected time horizons (Lower is better)
          </p>
        </div>

        {/* Timeframe & Filter Toggles */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Country Selector */}
          <select
            value={selectedCountry}
            onChange={e => setSelectedCountry(e.target.value)}
            className="text-xs font-semibold bg-slate-50 border border-slate-200 text-slate-700 rounded-xl px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option>United States</option>
            <option>United Kingdom</option>
            <option>Germany</option>
            <option>Japan</option>
            <option>Mexico</option>
            <option>France</option>
          </select>

          {/* Timeframe Pills */}
          <div className="inline-flex bg-slate-100/90 p-1 rounded-xl">
            {(['24H', '7D', '30D', '90D', '1Y', 'ALL'] as const).map(tf => (
              <button
                key={tf}
                onClick={() => setTimeframe(tf)}
                className={`px-2.5 py-1 text-xs font-semibold rounded-lg transition-all ${
                  timeframe === tf
                    ? 'bg-white text-blue-600 shadow-sm'
                    : 'text-slate-500 hover:text-slate-900'
                }`}
              >
                {tf}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* SVG Interactive Chart */}
      <div className="relative mt-4 w-full h-[260px]">
        <svg
          viewBox={`0 0 ${svgWidth} ${svgHeight}`}
          className="w-full h-full overflow-visible"
          onMouseLeave={() => setHoveredPoint(null)}
        >
          <defs>
            <linearGradient id="rankAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#1177ff" stopOpacity="0.28" />
              <stop offset="100%" stopColor="#1177ff" stopOpacity="0.0" />
            </linearGradient>
          </defs>

          {/* Horizontal Gridlines & Y-Axis Labels */}
          {yTicks.map(t => {
            const norm = (t - yMin) / Math.max(yMax - yMin, 1);
            const y = padY + norm * graphHeight;
            return (
              <g key={t}>
                <line
                  x1={padX}
                  y1={y}
                  x2={svgWidth - padX}
                  y2={y}
                  stroke="#f1f5f9"
                  strokeWidth="1"
                  strokeDasharray={t === 1 ? 'none' : '3 3'}
                />
                <text
                  x={padX - 8}
                  y={y + 3.5}
                  textAnchor="end"
                  fontSize="10"
                  fill="#94a3b8"
                  fontWeight="600"
                >
                  #{t}
                </text>
              </g>
            );
          })}

          {/* Area Fill */}
          {areaD && <path d={areaD} fill="url(#rankAreaGrad)" />}

          {/* Line Stroke */}
          {pathD && (
            <path
              d={pathD}
              fill="none"
              stroke="#1177ff"
              strokeWidth="2.75"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          )}

          {/* Points and Interaction Overlay */}
          {points.length === 0 ? (
            <text
              x={svgWidth / 2}
              y={svgHeight / 2}
              textAnchor="middle"
              fontSize="12"
              fill="#94a3b8"
              fontWeight="500"
            >
              No ranking history available for this timeframe
            </text>
          ) : (
            points.map((p, idx) => {
            const { x, y } = getCoordinates(idx, p.rank);
            const isHovered = hoveredPoint?.point === p;
            return (
              <g key={idx} className="cursor-pointer">
                {/* Hit area */}
                <circle
                  cx={x}
                  cy={y}
                  r="14"
                  fill="transparent"
                  onMouseEnter={() => setHoveredPoint({ point: p, x, y })}
                />
                {/* Visual Point */}
                {(isHovered || points.length <= 15 || idx === 0 || idx === points.length - 1) && (
                  <circle
                    cx={x}
                    cy={y}
                    r={isHovered ? 5.5 : 3.5}
                    fill={isHovered ? '#1177ff' : '#ffffff'}
                    stroke="#1177ff"
                    strokeWidth="2.5"
                    className="transition-all"
                  />
                )}
              </g>
            );
          })
          )}
        </svg>

        {/* Hover Tooltip Card */}
        {hoveredPoint && (
          <div
            className="absolute z-20 pointer-events-none bg-slate-900 text-white rounded-xl px-3 py-2 text-xs shadow-xl border border-slate-700/50 transform -translate-x-1/2 -translate-y-full mb-3"
            style={{
              left: `${(hoveredPoint.x / svgWidth) * 100}%`,
              top: `${(hoveredPoint.y / svgHeight) * 100}%`
            }}
          >
            <div className="font-bold text-sky-400 text-sm">Rank #{hoveredPoint.point.rank}</div>
            <div className="text-[11px] text-slate-300">Category: #{hoveredPoint.point.categoryRank}</div>
            <div className="text-[10px] text-slate-400 mt-0.5">{hoveredPoint.point.date}</div>
          </div>
        )}
      </div>

      {/* Date Axis Legend */}
      <div className="flex justify-between items-center text-[11px] text-slate-400 font-medium px-2 pt-2 border-t border-slate-100 mt-2">
        <span>{points[0]?.date || 'Start'}</span>
        <span className="text-slate-500 font-semibold">{timeframe} Period Trajectory</span>
        <span>{points[points.length - 1]?.date || 'Today'}</span>
      </div>
    </div>
  );
};
